import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Input, Modal, Select, Tag, Tooltip, message } from 'antd'
import {
  CloseOutlined,
  LeftOutlined,
  MailOutlined,
  MessageOutlined,
  PhoneOutlined,
  ReloadOutlined,
  RightOutlined,
} from '@ant-design/icons'
import styled from 'styled-components'
import Talk from 'talkjs'
import { API } from 'src/utils/interceptor'
import { publicExpressPost } from 'src/utils/publicExpressApi'
import { supplierNotifications } from 'src/features/ChecklistSlice'
import SalesAdvisorIcon, {
  SalesAdvisorMark,
} from 'src/components/icons/SalesAdvisorIcon'
import {
  SALES_ADVISOR_EMPTY_COPY,
  SALES_ADVISOR_PRODUCT_TITLE,
  SALES_ADVISOR_RETRY_COPY,
  buildOpportunityEmailDraft,
  formatRetrievedAt,
  provenanceLabel,
} from './salesAdvisorCopy'

const { TextArea } = Input

const PORTAL_PATHS = {
  insights: 'suppliers/sales-advisor/insights',
  events: 'suppliers/sales-advisor/events',
}

const CHECKLIST_PATHS = {
  insights: 'sales-advisor/checklist/insights',
  events: 'sales-advisor/checklist/events',
}

function salesAdvisorPaths(source) {
  return source === 'checklist' ? CHECKLIST_PATHS : PORTAL_PATHS
}

const CHECKLIST_REQUEST_TIMEOUT_MS = 120000

function postSalesAdvisor(source, path, body) {
  if (source === 'checklist') {
    return publicExpressPost(`/api/v1/${path}`, body, {
      timeout: CHECKLIST_REQUEST_TIMEOUT_MS,
    })
  }
  return API.post('api2', path, { body })
}

const TALKJS_APP_ID = 'GR4DzdvG'
const TALKJS_POPUP_Z_INDEX = '2147483000'

function ensureTalkJsAboveModals() {
  if (document.getElementById('oms-talkjs-stack')) return
  const style = document.createElement('style')
  style.id = 'oms-talkjs-stack'
  style.textContent = `
    .talkjs-container,
    iframe[title="TalkJS"],
    iframe[title*="TalkJS"],
    [class*="talkjs"],
    [id*="talkjs"] {
      z-index: ${TALKJS_POPUP_Z_INDEX} !important;
    }
  `
  document.head.appendChild(style)
}

function raiseTalkJsPopupZIndex() {
  document
    .querySelectorAll(
      '[class*="talkjs"], [id*="talkjs"], .talkjs-container, iframe[title*="TalkJS"]',
    )
    .forEach(node => {
      if (node && node.style) node.style.zIndex = TALKJS_POPUP_Z_INDEX
    })
}

async function openSupplierCustomerChat({
  supplierId,
  supplierTitle,
  venueId,
  venueTitle,
  popupRef,
}) {
  ensureTalkJsAboveModals()
  await Talk.ready

  if (popupRef.current) {
    try {
      popupRef.current.destroy()
    } catch (e) {
      // ignore
    }
    popupRef.current = null
  }

  if (window.talkSession) {
    try {
      window.talkSession.destroy()
    } catch (e) {
      // ignore
    }
    window.talkSession = null
  }

  const me = new Talk.User({
    id: String(supplierId),
    name: String(supplierTitle || 'Supplier'),
    welcomeMessage: 'Hey there! How are you? :-)',
    role: 'default',
  })
  const other = new Talk.User({
    id: String(venueId),
    name: String(venueTitle || 'Customer'),
    welcomeMessage: 'Hey there! Send us a message to start a chat 😁',
    role: 'venues',
  })

  window.talkSession = new Talk.Session({
    appId: TALKJS_APP_ID,
    me,
  })

  const conversation = window.talkSession.getOrCreateConversation(
    Talk.oneOnOneId(me, other),
  )
  conversation.setParticipant(me)
  conversation.setParticipant(other)

  const popup = window.talkSession.createPopup({ keepOpen: true })
  popupRef.current = popup
  popup.onSendMessage(() => {
    try {
      supplierNotifications(String(venueId), String(supplierId))
    } catch (e) {
      console.error('Error sending notification:', e)
    }
  })
  popup.select(conversation)
  await popup.mount({ show: true })
  if (typeof popup.show === 'function') popup.show()
  raiseTalkJsPopupZIndex()
  window.setTimeout(raiseTalkJsPopupZIndex, 300)
}

const SalesAdvisorModal = ({
  visible,
  venue,
  supplierId,
  supplierTitle,
  onClose,
  source = 'portal',
  checklist = null,
  fallbackEmail = '',
}) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [insight, setInsight] = useState(null)
  const [emailVisible, setEmailVisible] = useState(false)
  const [emailSubject, setEmailSubject] = useState('')
  const [emailBody, setEmailBody] = useState('')
  const [selectedEmail, setSelectedEmail] = useState(null)
  const [emailSending, setEmailSending] = useState(false)
  const [opportunityIndex, setOpportunityIndex] = useState(0)
  const popupRef = useRef(null)

  const venueId = venue?._id || venue?.venueId
  const venueTitle =
    venue?.venueTitle ||
    venue?.title ||
    insight?.contact?.venueTitle ||
    'Customer'
  const paths = salesAdvisorPaths(source)
  const isChecklist = source === 'checklist'
  const checklistSupplierId = checklist?.supplierId || supplierId
  const checklistDate = checklist?.date
  const checklistPurchaseOrderNumber = checklist?.purchaseOrderNumber
  const checklistToken = checklist?.token || undefined

  const loadInsight = useCallback(
    async ({ regenerate = false } = {}) => {
      if (!visible || !venueId) return
      const body = {
        venueId,
        regenerate,
        ...(isChecklist
          ? {
              supplierId: checklistSupplierId,
              date: checklistDate,
              purchaseOrderNumber: checklistPurchaseOrderNumber,
              token: checklistToken,
            }
          : {}),
      }
      setLoading(true)
      setError('')
      if (!regenerate) setInsight(null)
      try {
        const response = await postSalesAdvisor(source, paths.insights, body)
        if (!response?.success) {
          throw new Error(response?.message || SALES_ADVISOR_RETRY_COPY)
        }
        setInsight(response)
        postSalesAdvisor(source, paths.events, {
          ...body,
          eventName: 'insight_viewed',
          insightId: response.insightId,
          metadata: {
            insightId: response.insightId,
            cached: Boolean(response.cached),
            opportunityCount: (response.opportunities || []).length,
          },
        }).catch(() => {})
      } catch (err) {
        setError(
          err?.response?.data?.message ||
            err.message ||
            SALES_ADVISOR_RETRY_COPY,
        )
      } finally {
        setLoading(false)
      }
    },
    [
      visible,
      venueId,
      paths.insights,
      paths.events,
      isChecklist,
      checklistSupplierId,
      checklistDate,
      checklistPurchaseOrderNumber,
      checklistToken,
      source,
    ],
  )

  useEffect(() => {
    if (!visible || !venueId) {
      setInsight(null)
      setError('')
      setEmailVisible(false)
      setOpportunityIndex(0)
      return
    }
    loadInsight()
  }, [visible, venueId, loadInsight])

  const recordEvent = (eventName, metadata = {}) => {
    if (!venueId) return
    const insightId = insight?.insightId || metadata.insightId
    postSalesAdvisor(source, paths.events, {
      venueId,
      eventName,
      insightId,
      metadata: {
        ...metadata,
        insightId,
      },
      ...(isChecklist
        ? {
            supplierId: checklistSupplierId,
            date: checklistDate,
            purchaseOrderNumber: checklistPurchaseOrderNumber,
            token: checklistToken,
          }
        : {}),
    }).catch(() => {})
  }

  const emails = [...(insight?.contact?.emails || []), fallbackEmail].filter(
    (email, index, list) => {
      const trimmed = String(email || '').trim()
      if (!trimmed) return false
      return (
        list.findIndex(
          item =>
            String(item || '')
              .trim()
              .toLowerCase() === trimmed.toLowerCase(),
        ) === index
      )
    },
  )
  const phone = insight?.contact?.phone || ''
  const contactName = insight?.contact?.contactName || ''
  const opportunities = insight?.opportunities || []
  const questions = insight?.suggestedQuestions || []
  const opportunityIndexSafe =
    opportunities.length === 0
      ? 0
      : Math.min(opportunityIndex, opportunities.length - 1)
  const currentOpportunity = opportunities[opportunityIndexSafe] || null

  useEffect(() => {
    setOpportunityIndex(0)
  }, [insight])

  const openEmail = (opportunity = null) => {
    if (opportunity) {
      const draft = buildOpportunityEmailDraft({
        venueTitle,
        contactName,
        opportunity,
      })
      setEmailSubject(draft.subject)
      setEmailBody(draft.body)
      recordEvent('draft_email_clicked', {
        opportunityTitle: opportunity.title,
        opportunityIndex: opportunityIndexSafe,
      })
    } else {
      setEmailSubject('')
      setEmailBody('')
    }
    if (emails.length) {
      setSelectedEmail(emails[0])
    } else {
      setSelectedEmail(null)
    }
    setEmailVisible(true)
    recordEvent('contact_clicked', {
      channel: 'email',
      opportunityTitle: opportunity?.title || currentOpportunity?.title || '',
      opportunityIndex: opportunityIndexSafe,
    })
  }

  const sendEmail = async () => {
    if (!emailSubject.trim() || !emailBody.trim() || !selectedEmail) {
      message.error('Subject, body and recipient are required')
      return
    }
    setEmailSending(true)
    try {
      const path = isChecklist
        ? 'sales-advisor/checklist/email'
        : 'supplier/marketing/email/send-one'
      const response = await postSalesAdvisor(source, path, {
        supplierId: isChecklist ? checklistSupplierId : supplierId,
        venueId,
        subject: emailSubject.trim(),
        emailBody: emailBody.trim(),
        recipientEmail: selectedEmail,
        ...(isChecklist
          ? {
              date: checklistDate,
              purchaseOrderNumber: checklistPurchaseOrderNumber,
              token: checklistToken,
            }
          : {}),
      })
      if (response?.success) {
        message.success('Email sent successfully!')
        setEmailVisible(false)
      } else {
        message.error(
          response?.error || response?.message || 'Failed to send email',
        )
      }
    } catch (err) {
      message.error(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err.message ||
          'Failed to send email',
      )
    } finally {
      setEmailSending(false)
    }
  }

  const handleMessage = async () => {
    if (!supplierId || !supplierTitle || !venueId) {
      message.error('Supplier information not available. Please try again.')
      return
    }
    recordEvent('contact_clicked', {
      channel: 'message',
      opportunityTitle: currentOpportunity?.title || '',
      opportunityIndex: opportunityIndexSafe,
    })
    onClose()
    await new Promise(resolve => window.setTimeout(resolve, 50))
    try {
      await openSupplierCustomerChat({
        supplierId,
        supplierTitle,
        venueId,
        venueTitle,
        popupRef,
      })
    } catch (err) {
      console.error(err)
      message.error('Failed to open chat. Please try again.')
    }
  }

  const handleCall = () => {
    if (!phone) return
    recordEvent('contact_clicked', {
      channel: 'call',
      opportunityTitle: currentOpportunity?.title || '',
      opportunityIndex: opportunityIndexSafe,
    })
    window.location.href = `tel:${String(phone).replace(/\s+/g, '')}`
  }

  useEffect(() => {
    if (!visible) return undefined
    const onKeyDown = event => {
      if (emailVisible) return
      if (event.key === 'Escape') {
        onClose()
        return
      }
      if (
        event.key === 'ArrowRight' &&
        opportunityIndexSafe < opportunities.length - 1
      ) {
        setOpportunityIndex(index => index + 1)
      }
      if (event.key === 'ArrowLeft' && opportunityIndexSafe > 0) {
        setOpportunityIndex(index => index - 1)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [
    visible,
    emailVisible,
    onClose,
    opportunityIndexSafe,
    opportunities.length,
  ])

  if (!visible) {
    return null
  }

  return (
    <>
      <Overlay onClick={onClose} aria-hidden='false'>
        <Dialog
          role='dialog'
          aria-modal='true'
          aria-label={SALES_ADVISOR_PRODUCT_TITLE}
          $tall={Boolean(loading || error)}
          onClick={event => event.stopPropagation()}
        >
          <Header>
            <HeaderBrand>
              <SalesAdvisorIcon size={32} variant='inverse' />
              <HeaderText>
                <Title>{SALES_ADVISOR_PRODUCT_TITLE}</Title>
                <Subtitle>{venueTitle}</Subtitle>
              </HeaderText>
            </HeaderBrand>
            <HeaderActions>
              {insight && !loading ? (
                <HeaderGhost
                  type='button'
                  onClick={() => loadInsight({ regenerate: true })}
                >
                  <ReloadOutlined />
                  Regenerate
                </HeaderGhost>
              ) : null}
              <CloseButton
                type='button'
                onClick={onClose}
                aria-label={`Close ${SALES_ADVISOR_PRODUCT_TITLE}`}
              >
                <CloseOutlined />
              </CloseButton>
            </HeaderActions>
          </Header>

          <Body>
            {loading && (
              <LoadingWrap>
                <SalesAdvisorMark size={48} />
                <LoadingText>
                  Finding sales opportunities for this customer…
                </LoadingText>
              </LoadingWrap>
            )}

            {!loading && error && (
              <ErrorWrap>
                <p>{error}</p>
                <RetryButton
                  type='button'
                  onClick={() => loadInsight({ regenerate: true })}
                >
                  <ReloadOutlined />
                  Try again
                </RetryButton>
              </ErrorWrap>
            )}

            {!loading && !error && insight && (
              <MessageScroll>
                <MetaRow>
                  {insight.cached ? 'Saved briefing' : 'Just generated'}
                  {insight.generatedAt
                    ? ` · ${formatRetrievedAt(insight.generatedAt)}`
                    : ''}
                </MetaRow>

                {opportunities.length === 0 ? (
                  <EmptyWrap>
                    <p>{SALES_ADVISOR_EMPTY_COPY}</p>
                  </EmptyWrap>
                ) : (
                  <OpportunityCard>
                    <Pager>
                      <IntroLabel>
                        Opportunity {opportunityIndexSafe + 1} of{' '}
                        {opportunities.length}
                      </IntroLabel>
                      {opportunities.length > 1 ? (
                        <PagerButtons>
                          <PagerButton
                            type='button'
                            disabled={opportunityIndexSafe === 0}
                            onClick={() =>
                              setOpportunityIndex(index =>
                                Math.max(0, index - 1),
                              )
                            }
                            aria-label='Previous opportunity'
                          >
                            <LeftOutlined />
                          </PagerButton>
                          <PagerButton
                            type='button'
                            disabled={
                              opportunityIndexSafe >= opportunities.length - 1
                            }
                            onClick={() =>
                              setOpportunityIndex(index =>
                                Math.min(opportunities.length - 1, index + 1),
                              )
                            }
                            aria-label='Next opportunity'
                          >
                            <RightOutlined />
                          </PagerButton>
                        </PagerButtons>
                      ) : null}
                    </Pager>
                    <OpportunityTitle>
                      {currentOpportunity.title}
                    </OpportunityTitle>
                    <FieldLabel>Why we think this</FieldLabel>
                    <p>{currentOpportunity.why}</p>
                    <FieldLabel>What to do</FieldLabel>
                    <p>{currentOpportunity.whatToDo}</p>
                    <ChipRow>
                      {(currentOpportunity.provenance || []).map(
                        (item, provenanceIndex) => (
                          <Tooltip
                            key={`${item.kind}-${provenanceIndex}`}
                            title={
                              item.kind === 'public'
                                ? [
                                    item.url,
                                    formatRetrievedAt(item.retrievedAt),
                                  ]
                                    .filter(Boolean)
                                    .join(' · ')
                                : item.summary
                            }
                          >
                            <Tag color={chipColor(item.kind)}>
                              {provenanceLabel(item.kind)}
                            </Tag>
                          </Tooltip>
                        ),
                      )}
                    </ChipRow>
                    {(currentOpportunity.provenance || [])
                      .filter(item => item.kind === 'public' && item.url)
                      .map(item => (
                        <SourceLink
                          key={item.url}
                          href={item.url}
                          target='_blank'
                          rel='noopener noreferrer'
                        >
                          Source
                          {item.retrievedAt
                            ? ` · retrieved ${formatRetrievedAt(
                                item.retrievedAt,
                              )}`
                            : ''}
                        </SourceLink>
                      ))}
                  </OpportunityCard>
                )}

                {questions.length > 0 && (
                  <OpportunityCard>
                    <IntroLabel>Suggested conversation</IntroLabel>
                    <QuestionList>
                      {questions.map(question => (
                        <li key={question}>{question}</li>
                      ))}
                    </QuestionList>
                  </OpportunityCard>
                )}
              </MessageScroll>
            )}
          </Body>

          {!loading && !error && insight && (
            <Footer>
              <Actions>
                {phone ? (
                  <ContactButton type='button' onClick={handleCall}>
                    <ContactIcon>
                      <PhoneOutlined />
                    </ContactIcon>
                    <ContactCopy>
                      <span>Call</span>
                      <small>{phone}</small>
                    </ContactCopy>
                  </ContactButton>
                ) : null}
                <Tooltip
                  title={
                    currentOpportunity
                      ? 'Opens a ready-to-send email about this opportunity'
                      : emails.length
                      ? ''
                      : 'No email address is available for this customer.'
                  }
                >
                  <ContactButton
                    $primary
                    type='button'
                    onClick={event => {
                      event.stopPropagation()
                      openEmail(currentOpportunity)
                    }}
                  >
                    <ContactIcon $primary>
                      <MailOutlined />
                    </ContactIcon>
                    <ContactCopy>
                      <span>
                        {currentOpportunity
                          ? 'Email this opportunity'
                          : 'Email customer'}
                      </span>
                      <small>
                        {currentOpportunity
                          ? currentOpportunity.title
                          : emails.length
                          ? emails[0]
                          : 'No email on file'}
                      </small>
                    </ContactCopy>
                  </ContactButton>
                </Tooltip>
                <ContactButton
                  type='button'
                  onClick={event => {
                    event.stopPropagation()
                    handleMessage()
                  }}
                >
                  <ContactIcon>
                    <MessageOutlined />
                  </ContactIcon>
                  <ContactCopy>
                    <span>Message</span>
                    <small>Chat via OMS</small>
                  </ContactCopy>
                </ContactButton>
              </Actions>
            </Footer>
          )}
        </Dialog>
      </Overlay>

      <Modal
        visible={emailVisible}
        title={`Email ${venueTitle}`}
        onCancel={() => setEmailVisible(false)}
        footer={null}
        zIndex={1300}
      >
        <FieldLabel>To</FieldLabel>
        <Select
          style={{ width: '100%', marginBottom: 12 }}
          value={selectedEmail}
          onChange={setSelectedEmail}
        >
          {emails.map(email => (
            <Select.Option key={email} value={email}>
              {email}
            </Select.Option>
          ))}
        </Select>
        <FieldLabel>Subject</FieldLabel>
        <Input
          value={emailSubject}
          onChange={event => setEmailSubject(event.target.value)}
          style={{ marginBottom: 12 }}
        />
        <FieldLabel>Message</FieldLabel>
        <TextArea
          rows={8}
          value={emailBody}
          onChange={event => setEmailBody(event.target.value)}
        />
        <EmailFooter>
          <GhostButton type='button' onClick={() => setEmailVisible(false)}>
            Cancel
          </GhostButton>
          <RetryButton
            type='button'
            disabled={!selectedEmail || emailSending}
            onClick={sendEmail}
          >
            <MailOutlined />
            {emailSending ? 'Sending…' : 'Send email'}
          </RetryButton>
        </EmailFooter>
      </Modal>
    </>
  )
}

function chipColor(kind) {
  if (kind === 'oms') return 'green'
  if (kind === 'public') return 'blue'
  return 'default'
}

const SALES_ADVISOR_PANEL = {
  header: 'linear-gradient(135deg, #38bdf8 0%, #0284c7 58%, #0369a1 100%)',
  body: '#e0f2fe',
  ink: '#FFFFFF',
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 1100;
  animation: ollieOverlayIn 150ms ease-out;

  @keyframes ollieOverlayIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

const Dialog = styled.div`
  width: 100%;
  max-width: 720px;
  min-height: ${props =>
    props.$tall ? 'min(440px, calc(100vh - 40px))' : 'auto'};
  max-height: min(720px, calc(100vh - 40px));
  display: flex;
  flex-direction: column;
  background: ${SALES_ADVISOR_PANEL.body};
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.22);
  animation: ollieDialogIn 150ms ease-out;

  @keyframes ollieDialogIn {
    from {
      opacity: 0;
      transform: scale(0.96);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @media (max-width: 480px) {
    max-width: 100%;
    height: auto;
    max-height: calc(100vh - 24px);
    border-radius: 14px;
  }
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: ${SALES_ADVISOR_PANEL.header};
  color: ${SALES_ADVISOR_PANEL.ink};
  flex-shrink: 0;
`

const HeaderBrand = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
`

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
  color: ${SALES_ADVISOR_PANEL.ink};
`

const Subtitle = styled.p`
  margin: 2px 0 0;
  font-size: 13px;
  opacity: 0.88;
  color: ${SALES_ADVISOR_PANEL.ink};
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`

const HeaderGhost = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.18);
  color: ${SALES_ADVISOR_PANEL.ink};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.28);
  }
`

const CloseButton = styled.button`
  border: none;
  background: transparent;
  color: ${SALES_ADVISOR_PANEL.ink};
  font-size: 18px;
  cursor: pointer;
  padding: 4px;
  line-height: 1;

  &:hover {
    opacity: 0.85;
  }
`

const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 16px 20px 0;
`

const Footer = styled.div`
  flex-shrink: 0;
  background: ${({ theme }) => theme.colours?.mayoWhite || '#FFFFFF'};
  border-top: 1px solid ${({ theme }) => theme.colours?.panSilver || '#e0dfdf'};
  padding: 16px 20px max(32px, env(safe-area-inset-bottom, 0px));
`

const LoadingWrap = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 200px;
`

const LoadingText = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colours?.oliveGrey || '#919191'};
  font-size: 14px;
`

const ErrorWrap = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  min-height: 280px;
  text-align: center;
  padding: 32px 24px 48px;

  p {
    margin: 0;
    font-size: 15px;
    line-height: 1.5;
    color: ${({ theme }) => theme.colours?.bbqBlack || '#000000'};
  }
`

const MessageScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-bottom: 12px;
`

const MetaRow = styled.div`
  margin: 0 0 12px;
  font-size: 12px;
  color: ${({ theme }) => theme.colours?.oliveGrey || '#919191'};
`

const EmptyWrap = styled.div`
  background: ${({ theme }) => theme.colours?.mayoWhite || '#FFFFFF'};
  border: 1px solid ${({ theme }) => theme.colours?.panSilver || '#e0dfdf'};
  border-radius: 8px;
  padding: 10px 12px;
  text-align: center;
  color: ${({ theme }) => theme.colours?.bbqBlack || '#000000'};
  font-size: 13px;

  p {
    margin: 0 0 16px;
  }

  p:last-child {
    margin-bottom: 0;
  }
`

const OpportunityCard = styled.div`
  background: ${({ theme }) => theme.colours?.mayoWhite || '#FFFFFF'};
  border: 1px solid ${({ theme }) => theme.colours?.panSilver || '#e0dfdf'};
  border-radius: 14px 14px 14px 4px;
  padding: 14px 16px;
  margin-bottom: 14px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

  p {
    margin: 0 0 12px;
    font-size: 14px;
    line-height: 1.6;
    color: ${({ theme }) => theme.colours?.bbqBlack || '#000000'};
  }
`

const Pager = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 4px;
`

const PagerButtons = styled.div`
  display: flex;
  gap: 6px;
  flex-shrink: 0;
`

const PagerButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid #7dd3fc;
  border-radius: 8px;
  background: #f0f9ff;
  color: #0369a1;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #e0f2fe;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`

const IntroLabel = styled.span`
  display: block;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colours?.oliveGrey || '#919191'};
  margin-bottom: 4px;
`

const OpportunityTitle = styled.h4`
  margin: 0 0 12px;
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colours?.bbqBlack || '#000000'};
`

const FieldLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colours?.oliveGrey || '#919191'};
  margin-bottom: 4px;
`

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 8px;
`

const SourceLink = styled.a`
  display: block;
  margin-bottom: 12px;
  font-size: 13px;
`

const QuestionList = styled.ul`
  margin: 0;
  padding-left: 20px;
  color: ${({ theme }) => theme.colours?.bbqBlack || '#000000'};

  li {
    font-size: 14px;
    line-height: 1.6;
  }
`

const GhostButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  background: #ffffff;
  color: #334155;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: transform 0.15s ease, box-shadow 0.15s ease,
    border-color 0.15s ease, background 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    border-color: #cbd5e1;
    background: #f8fafc;
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
  }
`

const RetryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 18px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #38bdf8 0%, #0284c7 58%, #0369a1 100%);
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 10px 22px rgba(14, 165, 233, 0.32);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.06);
    box-shadow: 0 14px 28px rgba(14, 165, 233, 0.4);
  }

  &:disabled {
    background: #e2e8f0;
    color: #94a3b8;
    box-shadow: none;
    cursor: not-allowed;
  }
`

const Actions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px;
`

const ContactButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 64px;
  padding: 12px 14px;
  border: ${props => (props.$primary ? 'none' : '1.5px solid #7dd3fc')};
  border-radius: 14px;
  background: ${props =>
    props.$primary ? SALES_ADVISOR_PANEL.header : '#ffffff'};
  color: ${props => (props.$primary ? '#ffffff' : '#0369a1')};
  cursor: pointer;
  box-shadow: ${props =>
    props.$primary ? '0 10px 22px rgba(14, 165, 233, 0.32)' : 'none'};
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease,
    background 0.15s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: ${props => (props.$primary ? 'brightness(1.06)' : 'none')};
    background: ${props =>
      props.$primary ? SALES_ADVISOR_PANEL.header : '#f0f9ff'};
  }

  &:disabled {
    background: #e2e8f0;
    color: #94a3b8;
    border-color: #e2e8f0;
    box-shadow: none;
    cursor: not-allowed;
  }
`

const ContactIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 999px;
  background: ${props =>
    props.$primary ? 'rgba(255, 255, 255, 0.22)' : 'rgba(14, 165, 233, 0.12)'};
  color: ${props => (props.$primary ? '#ffffff' : '#0284c7')};
  font-size: 16px;
  flex-shrink: 0;

  ${ContactButton}:disabled & {
    background: rgba(148, 163, 184, 0.22);
    color: #94a3b8;
  }
`

const ContactCopy = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  min-width: 0;
  line-height: 1.25;

  span {
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.01em;
  }

  small {
    margin-top: 3px;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
    font-weight: 500;
    opacity: 0.88;
  }
`

const EmailFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
`

export default SalesAdvisorModal
